import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  Typography,
  Chip,
  CardActions,
  CardContent,
} from '@mui/material'
import RowActions, { Action } from '../Table/RowActions'
import { useTheme } from '@mui/material/styles'
import { styled } from '@mui/system'
import { GridDeleteIcon } from '@mui/x-data-grid'
import { FetchUserPlaylistsResponsePlaylistsPlaylistTypeEnum as playlistTypeEnum } from '../../swagger'
import { PLAYLIST_TEXT_TRUNCATION_LIMIT_CARD } from '@utils/constants'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import PlaylistSharedIcon from '@mui/icons-material/FolderShared'
import LearningCenterIcon from '@mui/icons-material/Book'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import {
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material'
import ManagePlaylistSharingModal from '@components/Modals/playlist/ManagePlaylistSharingModal'

interface ContentDetailPlaylistProps {
  name: string
  playlistKey: number
  playlistType: playlistTypeEnum
  isShared: boolean
  onDeleteClick: () => void
  onCopyPlaylist: () => void
  onRemoveSharedPlaylistClick: () => void
  onClick?: () => void
  highlighted?: boolean
}

const PlaylistCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})<{ highlighted?: boolean }>(({ theme, highlighted }) => ({
  width: 240,
  borderRadius: 8,
  transition: 'box-shadow 0.2s ease-in-out',
  border: highlighted ? `2px dashed ${theme.palette.success.light}` : 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: 240,
    marginTop: theme.spacing(2),
    '&:nth-of-type(1)': {
      marginTop: 0,
    },
  },
}))

const ContentDetailPlaylistCard: React.FC<ContentDetailPlaylistProps> = ({
  name,
  playlistKey,
  playlistType,
  isShared,
  onClick,
  onDeleteClick,
  onCopyPlaylist,
  onRemoveSharedPlaylistClick,
  highlighted,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [isOpenModal, setIsOpenModal] = useState(false)

  const playlistActions: Action<string>[] = useMemo(
    () => [
      {
        actionName: t(
          'ContentDetailPlaylist.PlaylistMenu.Action.Delete',
          'Delete'
        ),
        actionKey: 'deletePlaylist',
        actionFunction: onDeleteClick,
        menuItemIcon: <GridDeleteIcon />,
        hide: () => !!isShared,
      },
      {
        actionName: t(
          'ContentDetailPlaylist.PlaylistMenu.Action.Share',
          'Share'
        ),
        actionKey: 'sharePlaylist',
        actionFunction: () => setIsOpenModal(true),
        menuItemIcon: <ShareIcon />,
        hide: () => isShared,
      },
      {
        actionName: t('ContentDetailPlaylist.PlaylistMenu.Action.Copy', 'Copy'),
        actionKey: 'copyPlaylist',
        actionFunction: onCopyPlaylist,
        menuItemIcon: <ContentCopyIcon />,
        hide: () => !isShared,
      },
      {
        actionName: t(
          'ContentDetailPlaylist.PlaylistMenu.Action.Remove',
          'Remove'
        ),
        actionKey: 'removeSharedPlaylist',
        actionFunction: onRemoveSharedPlaylistClick,
        menuItemIcon: <GridDeleteIcon />,
        hide: () => !isShared,
      },
    ],
    [isShared, onCopyPlaylist, onDeleteClick, onRemoveSharedPlaylistClick, t]
  )

  return (
    <>
      <ManagePlaylistSharingModal
        key={`${playlistKey}-${isOpenModal}`}
        playlistKey={playlistKey}
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
      />
      <PlaylistCard highlighted={highlighted}>
        <CardContent sx={{ padding: 0, cursor: 'pointer' }}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={theme.spacing(15.7)}
              bgcolor={'#f4f4f4'}
              onClick={onClick}
            >
              {isShared ? (
                <PlaylistSharedIcon
                  sx={{
                    fontSize: theme.spacing(7),
                    color: 'grey',
                    opacity: 0.3,
                  }}
                />
              ) : (
                <PlaylistPlayIcon
                  sx={{
                    fontSize: theme.spacing(7),
                    color: 'grey',
                    opacity: 0.3,
                  }}
                />
              )}
            </Box>

            <Box sx={{ px: 2, pt: 1 }} onClick={onClick}>
              <Chip
                label={
                  isShared
                    ? t(
                        'Playlist.ContentDetailPlaylistCard.Chip.Label.Shared',
                        'SHARED'
                      )
                    : t(
                        'Playlist.ContentDetailPlaylistCard.Chip.Label.Owned',
                        'OWNED'
                      )
                }
                size="small"
                sx={{
                  bgcolor: isShared
                    ? theme.palette.success.light
                    : theme.palette.customBackground.mapButton,
                  color: 'white',
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  mb: 2,
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {name.length > PLAYLIST_TEXT_TRUNCATION_LIMIT_CARD
                  ? `${name.slice(0, PLAYLIST_TEXT_TRUNCATION_LIMIT_CARD)}...`
                  : name}
              </Typography>
            </Box>
          </Box>
        </CardContent>
        <CardActions sx={{ padding: 0 }}>
          <Box
            display="flex"
            px={1}
            alignItems="center"
            justifyContent="space-between"
            width={'100%'}
          >
            <Box>
              {playlistType === 'LearningCenter' ? (
                <LearningCenterIcon
                  sx={{
                    fontSize: 18,
                    color: theme.palette.grey[500],
                  }}
                />
              ) : (
                <BusinessCenterIcon
                  sx={{ fontSize: 18, color: theme.palette.grey[500] }}
                />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.grey[600],
                  ml: 1,
                  textTransform: 'capitalize',
                }}
              >
                {playlistType === 'LearningCenter'
                  ? 'Learning Center'
                  : 'Business Center'}
              </Typography>
            </Box>
            <RowActions
              elementsCount={playlistActions.length}
              actions={playlistActions}
              row={''}
              scrollToFirstStep
            />
          </Box>
        </CardActions>
      </PlaylistCard>
    </>
  )
}

export default ContentDetailPlaylistCard
